import React from 'react';
import ImageSlider from './ImageSlider';

import './PostContent.scss';
import { UserCapabilitiesContext } from '../lib/UserCapabilities';
import { Category } from '../data/Category';
import MultiLanguageContent from './MultiLanguageContent';
import { connect } from 'react-redux';
import { State } from '../data/rootReducer';
import { fetchPost, getPost } from '../data/postsSlice';
import { AppConnectedProps } from '../data/store';

type PostProps = AppConnectedProps<typeof connector> & {
  className?: string;
  category: Category;
  slug: string;
};

const connector = connect((state: State) => ({
  posts: state.posts,
}));

class PostContent extends React.Component<PostProps> {
  private cleanUpLocale: Function | null = null;

  componentDidMount() {
    this.props.dispatch(fetchPost(this.props.category, this.props.slug));
  }

  componentWillUnmount() {
    if (this.cleanUpLocale) {
      this.cleanUpLocale();
    }
  }

  render() {
    const post = getPost(this.props.posts, this.props.category, this.props.slug);
    if (post.status !== 'awake') {
      return <span>Loading...</span>;
    }

    const classes = ['post'];
    if (this.props.className) {
      classes.push(this.props.className);
    }

    return (
      <UserCapabilitiesContext.Consumer>
        {(userCapabilities) => {
          if (userCapabilities.isMobile()) {
            // Set a low resolution fallback as a background image for the first image.
            const firstImageStyle = {
              backgroundImage: `url('${post.item.thumbnail}')`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
            };

            const renderedImages = post.item.images.map((image, i) => (
              <img
                key={image.src}
                src={image.src}
                alt={post.item.title}
                width={image.width}
                height={image.height}
                style={i === 0 ? firstImageStyle : null}
              />
            ));

            return (
              <article className={[...classes, 'mobile'].join(' ')}>
                {renderedImages.length > 0 ? renderedImages[0] : null}
                {this.renderContent(post.item.content)}
                {renderedImages.slice(1)}
              </article>
            );
          }
          const direction = this.determineLayoutDirection(post.item.images);
          return (
            <article className={[...classes, 'desktop', direction].join(' ')}>
              {post.item.images.length > 0 ? (
                <ImageSlider
                  images={post.item.images.map((image, i) => ({
                    src: image.src,
                    alt: post.item.title,
                    fallbackSrc: i === 0 ? post.item.thumbnail : null,
                    width: image.width,
                    height: image.height,
                  }))}
                />
              ) : null}
              {this.renderContent(post.item.content)}
            </article>
          );
        }}
      </UserCapabilitiesContext.Consumer>
    );
  }

  renderContent(content: { en?: string; it?: string }) {
    return (
      <MultiLanguageContent
        className="text"
        category={this.props.category}
        slug={this.props.slug}
        content={content}
        key={JSON.stringify(content)} // XXX
      />
    );
  }

  determineLayoutDirection(
    images: Array<{ width: number; height: number }>
  ): 'horizontal' | 'vertical' {
    const ratios = images.map((image) => image.width / image.height).sort((a, b) => a - b);
    // At least one super-wide image.
    const highestRatio = ratios[ratios.length - 1];
    if (highestRatio > 2 / 1) {
      return 'horizontal';
    }
    // At least one super-tall image, and no super-wide image.
    const lowestRatio = ratios[0];
    if (lowestRatio < 1 / 2) {
      return 'vertical';
    }
    // Most images are vertical.
    const medianRatio = ratios[Math.floor(ratios.length / 2)];
    if (medianRatio < 3 / 4) {
      return 'vertical';
    }
    return 'horizontal';
  }
}

export default connector(PostContent);
